// Administración · Empresa (T-17p9, handoff §2, plan §2.2): los datos que
// aparecen en reportes, actas y correos a terceros, administrados por la
// propia organización bajo /administracion/empresa. Tres paneles sobre
// GET /organizations/me: Datos legales (razón social y RUT), Logo de la
// empresa y —sólo si la receta tiene marcas— Marcas con su logo.
//
// El único primario de la pantalla es «Guardar cambios» del panel Datos
// legales; el resto son secundarios y ghost. Lo que el handoff pide y el
// backend aún no da queda oculto, no simulado: el correo de respuesta y
// el código de 2 letras de la marca (sin columnas; esta ronda no migra).
//
// Gate de la pantalla: canAdministerOrg (RequireAdminModule en App.tsx,
// lo conecta el integrador); el backend repite la guarda
// (requireOrgAdmin, 403 ADMIN_REQUIRED).

import { useRef, useState, type RefObject } from "react";
import type { MyOrganizationBrand, MyOrganizationResponse } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageError } from "../../components/PageError";
import { PageHeader } from "../../components/PageHeader";
import { PageSkeleton } from "../../components/PageSkeleton";
import { Panel } from "../../components/Panel";
import { Skel } from "../../components/Skel";
import { DatosLegalesPanel } from "../../features/administracion/empresa/DatosLegalesPanel";
import {
  LogoDrawer,
  type LogoDrawerTarget,
} from "../../features/administracion/empresa/LogoDrawer";
import {
  MarcaDrawer,
  type MarcaDrawerMode,
} from "../../features/administracion/empresa/MarcaDrawer";
import { useRemoveOrgLogo } from "../../features/administracion/empresa/queries";
import { adminWords } from "../../lib/admin/words";
import { useMyOrganization, useRecipe } from "../../lib/api/queries";
import { useQueryView } from "../../lib/loading/useQueryView";
import { ADMIN_TOAST_OPTS, useToast } from "../../lib/useToast";
import { fmtDate } from "../../lib/util";
import { gateOf, RefreshNotices, SlowNotice } from "../queryViewKit";

const NO_BRANDS: MyOrganizationBrand[] = [];

export function Empresa() {
  const recipeQuery = useRecipe();
  const orgQuery = useMyOrganization();
  const toast = useToast();
  const removeLogo = useRemoveOrgLogo();

  const [logo, setLogo] = useState<LogoDrawerTarget | null>(null);
  const [marca, setMarca] = useState<MarcaDrawerMode | null>(null);
  // «Reemplazar / Subir logo» del panel: recibe el foco cuando «Quitar
  // logo» desaparece tras quitarlo, para que no caiga en <body>.
  const subirLogoRef = useRef<HTMLButtonElement>(null);

  const recipe = recipeQuery.data;
  const words = adminWords(recipe);
  const assetPluralLower = (recipe?.labels.assetPlural ?? "Activos").toLowerCase();
  const brandsEnabled = recipe?.brands?.enabled === true;
  const brandWords = recipe?.brands ?? { label: "Marca", pluralLabel: "Marcas" };

  // Las dos consultas son independientes y la pantalla necesita ambas (la
  // receta dice si hay marcas; la organización trae los datos): un solo
  // gate, el primer error gana, y la cabecera pinta desde el primer
  // render (ADR-0166).
  const gate = gateOf([useQueryView(recipeQuery), useQueryView(orgQuery)]);

  function retryAll() {
    for (const query of [recipeQuery, orgQuery]) {
      if (query.isError || query.isPending) void query.refetch();
    }
  }

  function saved(message: string) {
    setLogo(null);
    setMarca(null);
    toast.show(message, "ok", ADMIN_TOAST_OPTS);
  }

  function onQuitarLogo() {
    removeLogo.mutate(undefined, {
      onSuccess: () => {
        // El botón pulsado se desmonta con el refresh; el foco pasa al
        // vecino, que sigue montado y pasa a decir «Subir logo».
        subirLogoRef.current?.focus();
        toast.show(
          "Logo quitado · los reportes muestran el nombre de la empresa",
          "ok",
          ADMIN_TOAST_OPTS
        );
      },
      onError: () => {
        toast.show("No pudimos quitar el logo. Intenta de nuevo.", "err", ADMIN_TOAST_OPTS);
      },
    });
  }

  function renderBody() {
    if (gate.kind === "error") {
      return gate.offline ? (
        <PageError
          title="Sin conexión"
          description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
          onRetry={gate.retry}
        />
      ) : (
        <PageError
          title="No pudimos cargar los datos de la empresa"
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={gate.retry}
        />
      );
    }
    if (gate.kind !== "ready") {
      return (
        <>
          <PageSkeleton
            label="Cargando los datos de la empresa…"
            visible={gate.kind === "loading" && gate.visible}
          >
            <EmpresaSkeleton />
          </PageSkeleton>
          {gate.kind === "loading" && gate.slow && <SlowNotice onRetry={retryAll} />}
        </>
      );
    }

    const org = gate.data[1];
    const brands = org.brands ?? NO_BRANDS;
    const logoUrl = org.logoUrl ?? null;

    return (
      <>
        <RefreshNotices
          refreshFailed={gate.refreshFailed}
          revalidatingHint={gate.revalidatingHint}
          onRetry={retryAll}
        />
        <div className="grid-2">
          <DatosLegalesPanel org={org} onSaved={saved} />
          <LogoPanel
            org={org}
            subirRef={subirLogoRef}
            removing={removeLogo.isPending}
            onReemplazar={() => setLogo({ kind: "org", name: org.name, logoUrl })}
            onQuitar={onQuitarLogo}
          />
        </div>
        {brandsEnabled && (
          <div className="ag-mt16">
            <Panel
              title={brandWords.pluralLabel}
              sub={`${brands.length} ${(brands.length === 1 ? brandWords.label : brandWords.pluralLabel).toLowerCase()}`}
              actions={
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  title="Crear una marca nueva para agrupar y dar acceso por marca"
                  onClick={() => setMarca({ kind: "new" })}
                >
                  <Icon.Plus s={12} />
                  Agregar marca
                </button>
              }
            >
              {brands.length === 0 ? (
                <EmptyState
                  title={`Sin ${brandWords.pluralLabel.toLowerCase()}`}
                  description={`Agrega una ${brandWords.label.toLowerCase()} para agrupar ${assetPluralLower} e invitar a un gerente de marca.`}
                />
              ) : (
                <ul className="brand-list">
                  {brands.map((b) => (
                    <BrandRow
                      key={b.id}
                      brand={b}
                      onSubirLogo={() => setLogo({ kind: "brand", brand: b })}
                      onEditar={() => setMarca({ kind: "edit", brand: b })}
                    />
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.empresa}
        sub="Datos que aparecen en reportes, actas y correos a terceros."
      />
      {renderBody()}
      {logo && <LogoDrawer target={logo} onClose={() => setLogo(null)} onSaved={saved} />}
      {marca && (
        <MarcaDrawer
          mode={marca}
          assetPluralLower={assetPluralLower}
          onClose={() => setMarca(null)}
          onSaved={saved}
        />
      )}
    </div>
  );
}

// Panel «Logo de la empresa»: vista previa 80×80 sobre el damero, estado
// y las dos acciones del handoff. «Quitar logo» sólo cuando hay uno que
// quitar; sin logo, el botón dice «Subir logo» en vez de «Reemplazar» y
// su title deja de hablar de un logo actual que no existe.
function LogoPanel({
  org,
  subirRef,
  removing,
  onReemplazar,
  onQuitar,
}: {
  org: Pick<MyOrganizationResponse, "logoUrl" | "logoUpdatedAt">;
  subirRef: RefObject<HTMLButtonElement>;
  removing: boolean;
  onReemplazar: () => void;
  onQuitar: () => void;
}) {
  const logoUrl = org.logoUrl ?? null;
  return (
    <Panel title="Logo de la empresa" sub="PNG, JPG o WEBP · fondo transparente">
      <div className="brand-list__row">
        <div className="brand-list__preview">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo actual de la empresa" className="brand-list__img" />
          ) : (
            <span className="cell-meta">Sin logo</span>
          )}
        </div>
        <div className="brand-list__meta">
          <div className="ag-strong">
            {logoUrl
              ? org.logoUpdatedAt
                ? `Logo actual · actualizado el ${fmtDate(org.logoUpdatedAt)}`
                : "Logo actual"
              : "Sin logo · los reportes muestran el nombre de la empresa"}
          </div>
          <div className="ag-muted">Se usa en la portada de reportes y en el panel.</div>
        </div>
        <div className="brand-list__actions ag-row">
          <button
            ref={subirRef}
            type="button"
            className="btn btn--secondary btn--sm"
            title={
              logoUrl
                ? "Subir un archivo nuevo que reemplaza al actual"
                : "Subir el logo que aparece en reportes y en el panel"
            }
            disabled={removing}
            onClick={onReemplazar}
          >
            {logoUrl ? "Reemplazar" : "Subir logo"}
          </button>
          {logoUrl && (
            <button
              type="button"
              className="btn btn--danger btn--sm"
              title="Los reportes vuelven a mostrar solo el nombre"
              disabled={removing}
              onClick={onQuitar}
            >
              {removing ? "Quitando…" : "Quitar logo"}
            </button>
          )}
        </div>
      </div>
    </Panel>
  );
}

// Una marca de la lista: logo o «Sin logo», nombre, pill «Por defecto» y
// las dos acciones. Cada botón se nombra «{acción} {marca}» (aria-
// labelledby con dos ids) para que una lista no dé tres «Editar» iguales,
// como LogoUploadRow en /admin.
function BrandRow({
  brand,
  onSubirLogo,
  onEditar,
}: {
  brand: MyOrganizationBrand;
  onSubirLogo: () => void;
  onEditar: () => void;
}) {
  const nameId = `brand-name-${brand.id}`;
  const logoId = `brand-logo-${brand.id}`;
  const editId = `brand-edit-${brand.id}`;
  const logoUrl = brand.logoUrl ?? null;
  return (
    <li className="brand-list__row" role="group" aria-labelledby={nameId}>
      <div className="brand-list__preview">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="brand-list__img" />
        ) : (
          <span className="cell-meta">Sin logo</span>
        )}
      </div>
      <div className="brand-list__meta">
        <span id={nameId} className="ag-strong">
          {brand.name}
        </span>{" "}
        {brand.isDefault && <span className="st-pill st-pill--info">Por defecto</span>}
      </div>
      <div className="brand-list__actions ag-row">
        <button
          id={logoId}
          type="button"
          className="btn btn--ghost btn--sm"
          aria-labelledby={`${logoId} ${nameId}`}
          title="Logo que aparece en reportes de esta marca"
          onClick={onSubirLogo}
        >
          Subir logo
        </button>
        <button
          id={editId}
          type="button"
          className="btn btn--ghost btn--sm"
          aria-labelledby={`${editId} ${nameId}`}
          title="Cambiar el nombre de la marca"
          onClick={onEditar}
        >
          Editar
        </button>
      </div>
    </li>
  );
}

function EmpresaSkeleton() {
  return (
    <div className="grid-2" aria-hidden="true">
      <div className="panel">
        <div className="panel__head">
          <Skel variant="title" width="30%" />
        </div>
        <div className="panel__body">
          <Skel variant="text" width="40%" />
          <Skel height={38} />
          <Skel variant="text" width="20%" />
          <Skel height={38} />
        </div>
      </div>
      <div className="panel">
        <div className="panel__head">
          <Skel variant="title" width="40%" />
        </div>
        <div className="panel__body">
          <Skel variant="media" width={80} height={80} />
          <Skel variant="text" width="60%" />
        </div>
      </div>
    </div>
  );
}

export default Empresa;
